// External Dependencies
import { FC, useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import DownloadIcon from '@mui/icons-material/Download';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import Chip from '@mui/material/Chip';
import { AxiosError } from 'axios';
import Collapse from '@mui/material/Collapse';
import { EstimateShowResponse } from '@presto-assistant/api_types/api/admin/estimates';

// Internal Dependencies
import {
  DetailCard,
  EnhancedButton,
  ConfirmationDialog,
  ShowPageDataDisplay,
  EnhancedAlert,
} from 'components/shared';
import { useDownloadPurchaseOrder, useReviewPurchaseOrder } from 'hooks/api/purchaseOrders';

// Local Dependencies
import RejectPurchaseOrderDialog from './RejectPurchaseOrderDialog';

// Local Typings
interface PurchaseOrderCardProps {
  estimateId: string;
  purchaseOrder: EstimateShowResponse['purchaseOrders'][0];
}

// Component Definition
const PurchaseOrderCard: FC<PurchaseOrderCardProps> = ({
  estimateId,
  purchaseOrder,
}) => {
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);

  const { mutate: downloadPurchaseOrder } = useDownloadPurchaseOrder();
  const {
    error: reviewError,
    isLoading: isReviewing,
    mutate: reviewPurchaseOrder,
  } = useReviewPurchaseOrder(estimateId);

  const handleDownloadPurchaseOrder = useCallback(() => {
    if (purchaseOrder) {
      downloadPurchaseOrder(purchaseOrder.id);
    }
  }, [downloadPurchaseOrder, purchaseOrder]);

  const handleOpenApproveDialog = useCallback(() => {
    setIsApproveDialogOpen(true);
  }, []);

  const handleCloseApproveDialog = useCallback(() => {
    setIsApproveDialogOpen(false);
  }, []);

  const handleOpenRejectDialog = useCallback(() => {
    setIsRejectDialogOpen(true);
  }, []);

  const handleCloseRejectDialog = useCallback(() => {
    setIsRejectDialogOpen(false);
  }, []);

  const handleApprovePurchaseOrder = useCallback(() => {
    reviewPurchaseOrder({
      body: {
        isApproved: true,
        purchaseOrderId: purchaseOrder.id,
        rejectedReason: null,
      },
      params: { purchaseOrderId: purchaseOrder.id },
    });
    setIsApproveDialogOpen(false);
  }, [reviewPurchaseOrder, purchaseOrder]);

  if (!purchaseOrder) {
    return null;
  }

  // Determine if the purchase order has been reviewed
  const reviewedAt = purchaseOrder.approvedAt || purchaseOrder.rejectedAt;

  return (
    <>
      <DetailCard
        avatarAriaLabel="Purchase order info"
        avatarIcon={<LocalAtmIcon />}
        content={(
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
          >
            <Box
              alignItems="center"
              display="flex"
              gap={2}
            >
              <EnhancedButton
                color="primary"
                endIcon={<DownloadIcon />}
                onClick={handleDownloadPurchaseOrder}
                size="small"
                variant="outlined"
              >
                Download Purchase Order
              </EnhancedButton>
            </Box>

            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fit, minmax(300px, 1fr))"
              sx={{ gapX: 3, gapY: 2 }}
            >
              <ShowPageDataDisplay
                label="Status"
                value={(
                  <Box
                    alignItems="center"
                    display="flex"
                    gap={1}
                  >
                    {purchaseOrder.approvedAt && (
                      <Chip
                        color="success"
                        label="Approved"
                        size="small"
                        variant="outlined"
                      />
                    )}

                    {purchaseOrder.rejectedAt && (
                      <Chip
                        color="error"
                        label="Rejected"
                        size="small"
                        variant="outlined"
                      />
                    )}

                    {!purchaseOrder.approvedAt && !purchaseOrder.rejectedAt && (
                      <Chip
                        label="Pending"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  )}
              />

              <ShowPageDataDisplay
                label="Reviewed At"
                type="datetime"
                value={reviewedAt?.toString()}
              />
            </Box>

            {!reviewedAt && (
              <Box
                alignItems="center"
                display="flex"
                gap={2}
                justifyContent="flex-end"
              >
                <EnhancedButton
                  disabled={isReviewing}
                  endIcon={<ThumbDownIcon />}
                  onClick={handleOpenRejectDialog}
                  size="small"
                  variant="outlined"
                >
                  Reject
                </EnhancedButton>

                <EnhancedButton
                  disabled={isReviewing}
                  endIcon={<ThumbUpIcon />}
                  onClick={handleOpenApproveDialog}
                  size="small"
                  variant="contained"
                >
                  Approve
                </EnhancedButton>
              </Box>
            )}

            <Collapse in={Boolean(reviewError)}>
              <EnhancedAlert severity="error">
                {(reviewError as AxiosError | undefined)?.response?.data?.error ?? 'An error occurred while processing your request.'}
              </EnhancedAlert>
            </Collapse>
          </Box>
        )}
        subheader={purchaseOrder.purchaseOrderNumber}
        title="Purchase Order"
      />

      <ConfirmationDialog
        confirmButtonAction={handleApprovePurchaseOrder}
        confirmButtonText="Approve"
        description="Are you sure you want to approve this purchase order?"
        isSubmitting={isReviewing}
        onClose={handleCloseApproveDialog}
        open={isApproveDialogOpen}
        title="Approve Purchase Order"
      />

      <RejectPurchaseOrderDialog
        estimateId={estimateId}
        isOpen={isRejectDialogOpen}
        onClose={handleCloseRejectDialog}
        purchaseOrderId={purchaseOrder.id}
      />
    </>
  );
};

export default PurchaseOrderCard;
